/**
 * GLSL for the GPU field-smoothing pipeline (mosaicGlCompositor.ts's
 * despeckle + separable-exact-Gaussian FBO ping-pong) — the PRIMARY
 * smoothing path when the browser/driver supports a renderable float
 * render target (mosaicGlCapabilities.ts); mosaicGlFieldSmoothing.ts's CPU
 * worker path is the fallback when it doesn't. Two shaders:
 *
 *  - buildDespeckleFragmentShaderSource(): reads the R8/LUMINANCE raw
 *    texture, optionally despeckles (3x3 echo-neighbor count, same
 *    >=10dBZ/<3-neighbors rule as mosaicGlFieldSmoothing.ts's despeckle()),
 *    optionally maps no-echo cells to the low fill value (mapNoEchoAsLow's
 *    GPU equivalent) — both independently toggled via uniforms, matching
 *    fieldSmooth()'s own independent despeckle/sigma conditions exactly.
 *    Writes to an RGBA16F target (mosaicGlCompositor.ts) so the result
 *    carries more precision than the 8-bit source for the blur passes that
 *    read it next.
 *  - buildBlurFragmentShaderSource(): one separable 1D pass (horizontal or
 *    vertical, selected by which `u_texelStep` the caller uploads — same
 *    shader program for both directions) over a fixed BLUR_TAP_COUNT=11
 *    taps (mosaicGlFieldSmoothing.ts's MAX_BLUR_RADIUS=5, the largest
 *    radius any offered sigma needs), weights as named uniforms
 *    (u_w0..u_w10) recomputed from gaussianKernel1D + padKernelToBlurTapCount
 *    on sigma change — the EXACT Gaussian, not the CPU fallback's box-blur
 *    approximation (mosaicGlFieldSmoothing.ts's own doc comment on why the
 *    approximation is unnecessary once smoothing is cheap enough to run on
 *    the GPU every frame).
 *
 * Same GLSL ES 1.00 target and no-dynamic-indexing discipline as
 * mosaicGlShader.ts (every tap here is a separately-named, unrolled
 * statement) — reuses fusionShader.ts's compileShader/compileProgram
 * verbatim (re-exported from mosaicGlShader.ts already; imported from there
 * rather than fusionShader.ts a second time so there's one re-export
 * point), but deliberately does NOT reuse fusionShader.ts's own
 * VERTEX_SHADER_SOURCE — see UNFLIPPED_VERTEX_SHADER_SOURCE below for why.
 */
import { compileShader, compileProgram } from "./mosaicGlShader";
import { BLUR_TAP_COUNT, MAX_BLUR_RADIUS } from "./mosaicGlFieldSmoothing";

export { compileShader, compileProgram };

/** A fullscreen-quad vertex shader for the FBO ping-pong passes, WITHOUT
 * fusionShader.ts's VERTEX_SHADER_SOURCE's Y-flip
 * (`v_uv = vec2(x, 1.0 - y)`, chosen there so the DISPLAY pass's v_uv
 * matches fusionGeo.ts's top-left-origin window convention for its own
 * uvXform math).
 *
 * That flip is safe for the display pass because it only ever READS
 * through v_uv — its output goes straight to the canvas via rasterization,
 * never back through a sampler. But despeckle/blur passes both SAMPLE their
 * input via v_uv AND get their output WRITTEN at literal, unflipped
 * window/framebuffer coordinates (rasterization address, independent of
 * v_uv's own formula). Reusing the flipped vertex shader here means every
 * such pass effectively writes `output.texel(x, y) = input.texel(x, height
 * - 1 - y)` — a real Y-flip on every pass. Composed across the pipeline
 * (mosaicGlCompositor.ts's smoothOnGpu(): 1 pass for despeckle-only, 3 for
 * despeckle+blur), the flip parity is ODD either way, so the final
 * `smoothedTexture` ends up vertically mirrored relative to the raw texture
 * and relative to what render()'s uvXform-based sampling expects (which
 * matches the raw texture's own un-flipped, texImage2D-upload addressing —
 * proven correct by the CRISP/bypass path, which samples rawTexture
 * directly and has always rendered correctly). This produced exactly the
 * empty-viewport symptom: the display pass was sampling the mirrored half
 * of the smoothed texture, not the half the current geographic window
 * actually maps to. Using an UNFLIPPED v_uv here instead keeps every pass's
 * texel(x,y) addressed identically to its input's texel(x,y) — 0 net flip
 * regardless of how many passes run — so the smoothed result always shares
 * rawTexture's own addressing convention, matching what the display pass
 * (and fetchRawMask's texelCoord math) already assumes. */
export const UNFLIPPED_VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

/** The 8 neighbor offsets of a 3x3 neighborhood, excluding the center —
 * same "up to 8, out-of-bounds simply not counted" rule as
 * mosaicGlFieldSmoothing.ts's despeckle(). */
const NEIGHBOR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

function neighborCheckBlock(dx: number, dy: number): string {
  return `
  n = sampleRawAt(texelCoord + vec2(${dx.toFixed(1)}, ${dy.toFixed(1)}));
  if (n >= 0.0 && n >= u_floorNorm) echoCount++;`;
}

/** Despeckle + no-echo-as-low prep pass — reads `u_rawTex` (the R8/
 * LUMINANCE decoded grid, unchanged upload path), writes a value in [0,1]
 * (raw-byte-normalized, same convention mosaicGlShader.ts's display pass
 * already uses) to the R channel of an RGBA16F target.
 *
 * `u_despeckleEnabled` and `u_applyFill` are INDEPENDENT toggles (not one
 * "smoothing on/off" flag) because fieldSmooth()'s own two conditions are
 * independent: despeckle-without-blur (sigma=off, despeckle=true) must
 * despeckle but leave nodata as literal 0 (u_applyFill=0, preserving the
 * display pass's existing hard-nodata kernel behavior); blur-without-
 * despeckle (despeckle=false, sigma>0) must apply the fill but skip
 * neighbor-counting entirely. mosaicGlCompositor.ts sets both uniforms per
 * the same branching fieldSmooth() itself uses. */
export function buildDespeckleFragmentShaderSource(): string {
  const neighborChecks = NEIGHBOR_OFFSETS.map(([dx, dy]) => neighborCheckBlock(dx, dy)).join("\n");
  return `
precision highp float;
varying vec2 v_uv;

uniform sampler2D u_rawTex;      // R8/LUMINANCE original decoded grid
uniform vec2 u_texSize;          // width, height in texels
uniform float u_floorNorm;       // PRECIP_FLOOR_RAW/255 -- the >=10dBZ "echo" threshold (fusionCompositor.ts's PRECIP_FLOOR_RAW)
uniform float u_fillNorm;        // NO_ECHO_FILL_RAW/255 -- the no-echo-as-low fill value (mosaicGlFieldSmoothing.ts)
uniform int u_despeckleEnabled;  // 0/1 -- fieldSmooth()'s opts.despeckle
uniform int u_applyFill;         // 0/1 -- fieldSmooth()'s opts.sigma > 0 (whether this feeds the blur passes)

// Returns -1.0 (a sentinel outside the valid [0,1] normalized-raw range) for
// an out-of-bounds texel coordinate -- explicit bounds checking, NOT
// CLAMP_TO_EDGE wrap: repeating the edge texel would invent a phantom
// neighbor sharing the edge cell's own echo/non-echo status, biasing edge-
// cell despeckle decisions. Matches mosaicGlFieldSmoothing.ts's despeckle()
// CPU reference exactly ("out-of-bounds neighbors simply aren't counted").
float sampleRawAt(vec2 texelCoord) {
  if (texelCoord.x < 0.0 || texelCoord.x >= u_texSize.x || texelCoord.y < 0.0 || texelCoord.y >= u_texSize.y) return -1.0;
  vec2 uv = (texelCoord + 0.5) / u_texSize;
  return texture2D(u_rawTex, uv).r;
}

void main() {
  vec2 texelCoord = floor(v_uv * u_texSize);
  float raw = texture2D(u_rawTex, v_uv).r; // the current texel is always in-bounds by construction
  bool isEcho = raw >= u_floorNorm;
  float outRaw = raw;

  if (u_despeckleEnabled == 1 && isEcho) {
    int echoCount = 0;
    float n;
${neighborChecks}
    if (echoCount < 3) outRaw = 0.0;
  }

  float valueOut = outRaw;
  if (u_applyFill == 1) {
    valueOut = outRaw > 0.0 ? outRaw : u_fillNorm;
  }
  gl_FragColor = vec4(valueOut, 0.0, 0.0, 1.0);
}
`;
}

function blurTapBlock(offset: number, weightIndex: number): string {
  return `  sum += texture2D(u_src, v_uv + u_texelStep * ${offset.toFixed(1)}) * u_w${weightIndex};`;
}

/** One separable 1D blur pass — reused for BOTH the horizontal and vertical
 * direction by uploading a different `u_texelStep` ((1/width, 0) vs
 * (0, 1/height), mosaicGlCompositor.ts) rather than compiling two shader
 * variants. Fixed BLUR_TAP_COUNT (11) unrolled taps, weights as named
 * uniforms — no dynamic array indexing (this file's own top doc comment on
 * why), reused from smaller sigmas via padKernelToBlurTapCount's zero
 * padding (mosaicGlFieldSmoothing.ts) rather than a shorter shader. */
export function buildBlurFragmentShaderSource(): string {
  const radius = MAX_BLUR_RADIUS;
  const taps = Array.from({ length: BLUR_TAP_COUNT }, (_, i) => blurTapBlock(i - radius, i)).join("\n");
  const weightUniforms = Array.from({ length: BLUR_TAP_COUNT }, (_, i) => `uniform float u_w${i};`).join("\n");

  return `
precision highp float;
varying vec2 v_uv;

uniform sampler2D u_src;   // the pass this reads from (despeckle output for the horizontal pass, its own horizontal output for the vertical pass)
uniform vec2 u_texelStep;  // (1/width, 0) for the horizontal pass, (0, 1/height) for the vertical pass
${weightUniforms}

void main() {
  vec4 sum = vec4(0.0);
${taps}
  gl_FragColor = sum;
}
`;
}
